import { Vehicle } from 'further-types/lp/vehicle';
import { Grid } from '@mui/material';
import PageSection from 'components/PageSection';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { AssetType } from 'further-types/lp/asset';
import ErrorBoundary from 'components/ErrorBoundary';
import RecordView from 'components/RecordView';
import { Divider } from '@mui/material';
import Spacing from 'components/Spacing';
import HoldingClassRealisation from './HoldingClassRealisation';
import { defaultHoldingClassRealisation } from '../FormWrapper';
import RealisationFormFields from './RealisationFormFields';
import Button from 'components/Button';
import React, { useMemo } from 'react';
import ButtonGroup from 'components/ButtonGroup';
import useSyncInvestorsWithholdingTax from '../hooks/useSyncInvestorsWithholdingTax';
import useClearConsiderationForm from '../hooks/useClearConsiderationForm';
import { sumBy, uniq, compact } from 'lodash';
import { useNotification } from 'hooks/ui/useNotification';

type CreateRealisationProps = {
  onSuccess: () => void;
  selectedVehicle: Vehicle;
};

const CreateRealisation: React.FC<CreateRealisationProps> = ({
  onSuccess,
  selectedVehicle,
}) => {
  const { control, watch, handleSubmit, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();

  const { setInvestorWithholdingTaxList } = useSyncInvestorsWithholdingTax();

  const selectedAssetId = watch('assetId');
  const holdingClassRealisations = watch('holdingClassRealisation');

  const notification = useNotification();

  // Extract all investment IDs being realised
  const realisedInvestmentIds = useMemo(() => {
    const ids = holdingClassRealisations?.flatMap(
      (holdingClass) =>
        holdingClass.unitsAllocationPerInvestment
          ?.filter((allocation) => allocation.allocatedUnits > 0)
          .map((allocation) => allocation.investmentId ?? '') ?? [],
    );

    return uniq(compact(ids));
  }, [holdingClassRealisations]);

  const { data: lastPostingDate } =
    api.unrealisedAssetFxGainOrLoss.getLastPostingDateByInvestments.useQuery(
      {
        vehicleId: selectedVehicle._id,
        investmentIds: realisedInvestmentIds,
      },
      {
        enabled: realisedInvestmentIds.length > 0,
      },
    );

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'holdingClassRealisation',
  });

  const { clearConsiderationForm, debouncedClearConsiderationForm } =
    useClearConsiderationForm({
      vehicleCurrencyCode: selectedVehicle.baseCurrency,
    });

  const [assets] = api.asset.list.useSuspenseQuery({
    assetType: AssetType.Company,
    order: 'desc',
    orderBy: 'createdAt',
    vehicleId: selectedVehicle._id,
  });

  const assetOptions = assets?.result.map((asset) => ({
    id: asset._id,
    title: asset.assetName,
  }));

  const onError = (errors: any) => {
    if (errors['']?.message) {
      notification.error(errors[''].message);
      return;
    }

    notification.error('There are validation errors. Please review the form.');
  };

  const realisationDate = watch('realisationDate');

  const validateUnitsSold = () => {
    const holdingClassRealisations = getValues('holdingClassRealisation');

    const totalUnitsSold = sumBy(holdingClassRealisations, 'unitsSold');

    const unitsAllocationPerInvestment = holdingClassRealisations.flatMap(
      (holdingClassRealisation) =>
        holdingClassRealisation.unitsAllocationPerInvestment ?? [],
    );

    const totalUnitsSoldAllocationPerInvestment = sumBy(
      unitsAllocationPerInvestment,
      'allocatedUnits',
    );

    const result =
      totalUnitsSold !== totalUnitsSoldAllocationPerInvestment
        ? {
            isValid: false,
            message:
              'The total number of units sold is not equal to the sum of the allocated units per investment',
          }
        : {
            isValid: true,
            message: '',
          };

    return result;
  };

  const validateRealisationDate = () => {
    if (lastPostingDate && realisationDate) {
      if (realisationDate.isBefore(lastPostingDate)) {
        return {
          isValid: false,
          message: `Cannot post a realisation with a date (${realisationDate.toLabel()}) before the last unrealised gains/losses posting date (${lastPostingDate.toLabel()}) for the investments being realised. Please select a date on or after the last posting.`,
        };
      }
    }
    return {
      isValid: true,
      message: '',
    };
  };

  const onSubmit = handleSubmit(() => {
    const isUnitsSoldValid = validateUnitsSold();

    if (!isUnitsSoldValid.isValid) {
      notification.error(isUnitsSoldValid.message);
      return;
    }

    const validationResult = validateRealisationDate();
    if (!validationResult?.isValid) {
      notification.error(validationResult.message);
      return;
    }

    setInvestorWithholdingTaxList();
    onSuccess();
  }, onError);

  const { data: holdingClasses } = api.holdingClass.list.useQuery(
    {
      assetId: selectedAssetId ?? '',
      order: 'desc',
      orderBy: 'createdAt',
      vehicleId: selectedVehicle._id,
    },
    {
      enabled: !!selectedAssetId,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );

  const { data: investmentHistory } = api.asset.getInvestmentHistory.useQuery(
    {
      assetId: selectedAssetId,
      vehicleId: selectedVehicle._id,
      date: realisationDate,
      order: 'desc',
      orderBy: 'dealCompletionDate',
    },
    {
      enabled: !!selectedAssetId,
    },
  );

  const { data: holdingClassTotals } =
    api.asset.getAssetHoldingClassInvestmentTotals.useQuery(
      {
        assetId: selectedAssetId ?? '',
        vehicleId: selectedVehicle._id,
        date: realisationDate,
      },
      {
        enabled: !!selectedAssetId,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
      },
    );

  const onAddHoldingClass = () => {
    append(defaultHoldingClassRealisation);
    clearConsiderationForm();
  };

  const onRemoveHoldingClass = (index: number) => {
    remove(index);
    clearConsiderationForm();
  };

  const holdingClassOptions =
    holdingClasses?.result.map((holdingClass) => ({
      value: holdingClass._id,
      label: holdingClass.name,
    })) ?? [];

  const getAvailableHoldingClassOptions = (index: number) => {
    const selectedIds = fields
      .map((item, idx) => (idx !== index ? item.holdingClassId : null))
      .filter((id) => id !== null && id !== '');

    return holdingClassOptions.filter(
      (holdingClass) => !selectedIds.includes(holdingClass.value),
    );
  };

  return (
    <ErrorBoundary>
      <PageSection title="Enter realisation details">
        <Spacing>
          <Grid container>
            <Grid size={{ xs: 12, md: 8 }}>
              <RecordView>
                <RealisationFormFields
                  selectedVehicle={selectedVehicle}
                  assetOptions={assetOptions ?? []}
                  replace={replace}
                  hasHoldingClassesAddedOnDeal={getValues(
                    'holdingClassRealisation',
                  ).some((field) => field.holdingClassId !== '')}
                />

                {fields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <Divider />
                    <HoldingClassRealisation
                      key={`holding-class-${field.id}`}
                      index={index}
                      control={control}
                      holdingClassOptions={getAvailableHoldingClassOptions(
                        index,
                      )}
                      holdingClassTotals={holdingClassTotals ?? []}
                      investmentHistory={investmentHistory ?? []}
                      onAddHoldingClass={onAddHoldingClass}
                      onRemoveHoldingClass={() => onRemoveHoldingClass(index)}
                      clearConsiderationForm={debouncedClearConsiderationForm}
                      isLast={index === fields.length - 1}
                      isFirst={index === 0}
                    />
                  </React.Fragment>
                ))}
                <ButtonGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onSubmit()}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              </RecordView>
            </Grid>
          </Grid>
        </Spacing>
      </PageSection>
    </ErrorBoundary>
  );
};

export default CreateRealisation;
